import * as React from 'react';
import { Check } from '@phosphor-icons/react';

import { FontOption } from './types';
import { loadFont } from './font-utils';

import { cn } from '@/lib/utils';
import { CommandItem } from '@/components/ui/command';

export interface FontPickerItemProps {
  font: FontOption;
  isSelected: boolean;
  onSelect: () => void;
}

/**
 * Individual font item in the dropdown list
 * Loads font on mount for preview using Intersection Observer
 * Memoized to prevent unnecessary re-renders
 */
export const FontPickerItem = React.memo(function FontPickerItem({ font, isSelected, onSelect }: FontPickerItemProps) {
  const itemRef = React.useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = React.useState(false);

  // Use Intersection Observer to lazy-load fonts
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (itemRef.current) {
      observer.observe(itemRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Load font when visible
  React.useEffect(() => {
    if (isVisible) {
      loadFont(font.value);
    }
  }, [isVisible, font.value]);

  // Build accessible label
  const ariaLabel = `Select ${font.label} font${font.category ? `, ${font.category} category` : ''}`;

  return (
    <CommandItem
      ref={itemRef}
      value={font.label}
      onSelect={onSelect}
      className="cursor-pointer"
      aria-label={ariaLabel}
      aria-selected={isSelected}
    >
      <span style={{ fontFamily: isVisible ? font.value : undefined }} className="flex-1 truncate">
        {font.label}
      </span>
      {font.category && <span className="text-xs text-muted-foreground capitalize">{font.category}</span>}
      <Check className={cn('ml-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
    </CommandItem>
  );
});
